/**
 * 🎉 Hook pour la détection de nouveaux badges
 * Déclenche des effets visuels et sonores quand un badge est débloqué
 */

import { useEffect, useRef, useCallback } from 'react';
import { calculateBadges } from '../components/CustomerStats';
import soundSystem from './sounds';
import { launchConfetti, launchBadgeConfetti } from './confetti';

/**
 * Hook pour détecter et célébrer les nouveaux badges
 * @param {Object} customer - Données du client
 * @param {Array} cards - Cartes de fidélité du client
 * @param {Function} onBadgeUnlocked - Callback quand un badge est débloqué
 */
export function useBadgeDetection(customer, cards = [], onBadgeUnlocked = null) {
  const previousBadgesRef = useRef([]);
  const isInitializedRef = useRef(false);

  const celebrateBadge = useCallback((badge) => {
    // Jouer le son de badge débloqué
    soundSystem.playBadgeUnlocked();
    
    // Lancer les confettis spéciaux badge
    launchBadgeConfetti(badge.color);
    
    // Callback externe si fourni
    if (onBadgeUnlocked) {
      onBadgeUnlocked(badge);
    }
  }, [onBadgeUnlocked]);

  useEffect(() => {
    if (!customer) return;

    const currentBadges = calculateBadges(customer, cards);
    const currentBadgeIds = currentBadges.map(b => b.id);
    const previousBadgeIds = previousBadgesRef.current;

    // Ne pas déclencher au premier chargement
    if (!isInitializedRef.current) {
      previousBadgesRef.current = currentBadgeIds;
      isInitializedRef.current = true;
      return;
    }

    // Trouver les nouveaux badges
    const newBadgeIds = currentBadgeIds.filter(id => !previousBadgeIds.includes(id));

    // Célébrer chaque nouveau badge
    newBadgeIds.forEach(badgeId => {
      const badge = currentBadges.find(b => b.id === badgeId);
      if (badge) {
        // Délai pour ne pas surcharger
        setTimeout(() => celebrateBadge(badge), 500);
      }
    });

    // Mettre à jour la référence
    previousBadgesRef.current = currentBadgeIds;
  }, [customer, cards, celebrateBadge]);

  return {
    badges: calculateBadges(customer, cards),
  };
}

/**
 * Hook pour jouer un son à l'obtention d'un tampon
 * @param {Array} cards - Cartes de fidélité
 */
export function useStampSound(cards = []) {
  const previousStampCountRef = useRef(0);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Calculer le nombre total de tampons
    const currentStampCount = cards.reduce((acc, card) => {
      return acc + (card.boxes?.filter(b => b).length || 0);
    }, 0);

    // Ne pas jouer au premier chargement
    if (!isInitializedRef.current) {
      previousStampCountRef.current = currentStampCount;
      isInitializedRef.current = true;
      return;
    }

    // Si on a plus de tampons qu'avant, jouer le son
    if (currentStampCount > previousStampCountRef.current) {
      soundSystem.playStamp();
      
      // Petit effet confetti pour le tampon
      launchConfetti({
        particleCount: 30,
        spread: 60,
        origin: { y: 0.7 },
        colors: ['#fbbf24', '#f97316', '#22c55e'],
      });
    }

    previousStampCountRef.current = currentStampCount;
  }, [cards]);

  return {
    totalStamps: cards.reduce((acc, card) => acc + (card.boxes?.filter(b => b).length || 0), 0),
  };
}

/**
 * Hook pour jouer un son de match Heartbeats
 */
export function useMatchSound() {
  const playMatchSound = useCallback(() => {
    soundSystem.playMatch();
    launchConfetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#ec4899', '#a855f7', '#8b5cf6', '#f472b6'],
    });
  }, []);

  return { playMatchSound };
}

export default { useBadgeDetection, useStampSound, useMatchSound };
